import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { PageSeoService } from './page-seo';

/**
 * Sets the browser tab/history title as "PayHOA | <Page>".
 *
 * The page name is resolved dynamically — no per-route config required:
 *   1. If a route declares an explicit `title` (e.g. `title: 'Unit Detail'`),
 *      that wins. Use this to override the derived name for a specific page.
 *   2. Otherwise the page's own heading, once it renders: the `ph-page-header`
 *      title, else the first `h1` outside a dialog. Watched until it appears
 *      (up to 20 seconds, since a cold load can draw it late), then 3 more.
 *   3. Until then (or if there is none) the name is derived from the URL — the
 *      last meaningful path segment, humanized. e.g.
 *        /app/chart-of-accounts            -> "PayHOA | Chart of Accounts"
 *        /app/reports/cash-flow-statement  -> "PayHOA | Cash Flow Statement"
 *        /app/units/1234                   -> "PayHOA | Units"  (id segment skipped)
 *   4. If nothing meaningful is found, falls back to the product title, so
 *      behaviour is never worse than before.
 *
 * It also applies the route's `data.seo` (description, `noindex`, canonical) through
 * `PageSeoService`, here because this runs exactly once per navigation with the final snapshot.
 */
@Injectable({ providedIn: 'root' })
export class PayhoaTitleStrategy extends TitleStrategy {
    private static readonly PREFIX = 'PayHOA';

    private static readonly OVERLAYS = 'ph-dialog, ph-drawer, [role="dialog"], .cdk-overlay-container';

    private static readonly FALLBACK = 'Simple HOA Management Software';

    /** Layout/context segments that aren't real "pages". */
    private static readonly IGNORED_SEGMENTS = new Set(['app', 'admin', 'pmc', 'events']);

    /** Short connector words kept lowercase in the middle of a title. */
    private static readonly MINOR_WORDS = new Set(['of', 'vs', 'by', 'and', 'to', 'the', 'for', 'in', 'on', 'a', 'an']);

    /** The heading is watched until it shows up (then a few seconds more, for a late rename), never past the cap. */
    private static readonly HEADING_SETTLE_MS = 3000;
    private static readonly HEADING_CAP_MS = 20000;

    private stopWatching: (() => void) | null = null;

    constructor(
        private readonly title: Title,
        private readonly pageSeo: PageSeoService,
        private readonly zone: NgZone,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        super();
    }

    override updateTitle(snapshot: RouterStateSnapshot): void {
        this.stopWatching?.();
        const declared = this.buildTitle(snapshot);
        this.setPage(declared ?? this.pageHeading() ?? this.deriveFromUrl(snapshot.url));
        if (!declared) {
            this.watchHeading();
        }
        this.pageSeo.apply(snapshot);
    }

    private setPage(page: string | undefined): void {
        this.title.setTitle(`${PayhoaTitleStrategy.PREFIX} | ${page || PayhoaTitleStrategy.FALLBACK}`);
    }

    /** Follow the page's heading as it renders, for a few seconds after the navigation. */
    private watchHeading(): void {
        const view = this.document.defaultView;
        if (!view?.MutationObserver || !this.document.body) {
            return;
        }

        this.zone.runOutsideAngular(() => {
            let frame = 0;
            let settle = 0;
            const check = () => {
                frame = 0;
                const heading = this.pageHeading();
                if (heading && this.title.getTitle() !== `${PayhoaTitleStrategy.PREFIX} | ${heading}`) {
                    this.setPage(heading);
                }
                if (heading && !settle) {
                    settle = view.setTimeout(() => this.stopWatching?.(), PayhoaTitleStrategy.HEADING_SETTLE_MS);
                }
            };
            const observer = new view.MutationObserver(() => {
                frame ||= view.requestAnimationFrame(check);
            });
            observer.observe(this.document.body, { childList: true, subtree: true, characterData: true });
            const timer = view.setTimeout(() => this.stopWatching?.(), PayhoaTitleStrategy.HEADING_CAP_MS);
            this.stopWatching = () => {
                observer.disconnect();
                view.clearTimeout(timer);
                view.clearTimeout(settle);
                view.cancelAnimationFrame(frame);
                this.stopWatching = null;
            };
        });
    }

    /** The page's own title: the kit page header's text, else the first h1 outside a dialog. */
    private pageHeading(): string | undefined {
        const header = Array.from(this.document.querySelectorAll('ph-page-header'))
            .find(el => !el.closest(PayhoaTitleStrategy.OVERLAYS));
        if (header) {
            const text = Array.from(header.childNodes)
                .filter(node => node.nodeType === Node.TEXT_NODE || (node instanceof Element && !node.hasAttribute('slot')))
                .map(node => node.textContent)
                .join(' ');
            const clean = this.clean(text);
            if (clean) {
                return clean;
            }
        }

        const h1 = Array.from(this.document.querySelectorAll('h1'))
            .find(el => !el.closest(PayhoaTitleStrategy.OVERLAYS));

        return this.clean(h1?.textContent);
    }

    private clean(text: string | null | undefined): string | undefined {
        const value = (text ?? '').replace(/\s+/g, ' ').trim();

        return value.length > 0 && value.length <= 80 ? value : undefined;
    }

    /** Pull a human-readable page name from the last meaningful URL segment(s). */
    private deriveFromUrl(url: string): string | undefined {
        const segments = url
            .split(/[?#]/)[0] // drop query string / fragment
            .split('/')
            .map(segment => segment.split(';')[0]) // drop matrix params
            // A route kept on a -v2 URL while its flag rolls out is still just "Violations" to
            // the person reading the tab; the version suffix is ours, not theirs.
            .map(segment => segment.replace(/-v\d+$/, ''))
            .filter(segment => segment.length > 0
                && !PayhoaTitleStrategy.IGNORED_SEGMENTS.has(segment)
                && !this.looksLikeId(segment));

        const lastSegment = segments[segments.length - 1];
        const parentSegment = segments[segments.length - 2];

        if (!lastSegment) {
            return undefined;
        }

        // When the last segment is just a qualifier (e.g. .../unit-balance/detail
        // or .../payments/by-unit), include the parent so the title stays meaningful.
        if (parentSegment && this.isQualifier(lastSegment)) {
            // Humanize the joined string (not each piece) so minor-word casing
            // spans the boundary: payments/by-unit -> "Payments by Unit".
            return this.humanize(`${parentSegment}-${lastSegment}`);
        }

        return this.humanize(lastSegment);
    }

    /**
     * Trailing segments that read poorly on their own, so the parent segment is
     * prepended for context (e.g. .../bank-reconciliation/report -> "Bank Reconciliation Report").
     * Covers generic verbs (edit/create/...) and common closing nouns/states seen in our routes.
     */
    private isQualifier(segment: string): boolean {
        return segment.startsWith('by-')
            || [
                'detail', 'details', 'summary', 'list', 'new', 'edit', 'create', 'overview', 'archived',
                'dashboard', 'enroll', 'enrollment', 'scheduled', 'report', 'confirmation',
                'reschedule', 'cancel', 'homeowner', 'drafts', 'bookings',
            ].includes(segment);
    }

    /** Numeric ids and UUIDs aren't page names. */
    private looksLikeId(segment: string): boolean {
        return /^\d+$/.test(segment)
            || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
    }

    /** "cash-flow-statement" -> "Cash Flow Statement", "profit-vs-loss" -> "Profit vs Loss". */
    private humanize(segment: string): string {
        return segment
            .split('-')
            .map((word, index) => {
                if (index > 0 && PayhoaTitleStrategy.MINOR_WORDS.has(word)) {
                    return word;
                }

                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(' ');
    }
}
